/** Self-serve account profile + password change.
 *
 * Available to every authenticated user — no permission gate. Two
 * cards: profile (first/last name) and password change. Email is
 * shown read-only; changing it touches invite trust + needs its own
 * verification flow.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate } from "@/lib/format";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AppLayout } from "@/components/AppLayout";
import { PasswordInput } from "@/components/PasswordInput";
import { ApiPaths, api } from "@/lib/api";
import { useMe } from "@/lib/auth";
import type { User } from "@/lib/types";
import {
  browserSupportsWebAuthn,
  deletePasskey,
  listPasskeys,
  registerPasskey,
} from "@/lib/webauthn";
import { BackupCodesPanel } from "@/modules/auth/BackupCodesPanel";
import { MfaSetupModal } from "@/modules/auth/MfaSetupModal";


function ProfileCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useMe();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  useEffect(() => {
    if (me.data?.user) {
      setFirstName(me.data.user.first_name ?? "");
      setLastName(me.data.user.last_name ?? "");
    }
  }, [me.data]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await api.patch<User>(ApiPaths.userProfile, {
        first_name: firstName,
        last_name: lastName,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });

  if (!me.data?.user) return null;

  const dirty =
    firstName !== (me.data.user.first_name ?? "") ||
    lastName !== (me.data.user.last_name ?? "");

  return (
    <section className="card space-y-3">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("account.profile.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">{t("account.profile.cardHint")}</p>
      </header>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>{t("account.profile.firstNameLabel")}</span>
          <input
            type="text"
            className="input w-full"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={150}
          />
        </label>
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>{t("account.profile.lastNameLabel")}</span>
          <input
            type="text"
            className="input w-full"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            maxLength={150}
          />
        </label>
      </div>
      <div className="text-xs text-nb-text-3">
        <span>{t("account.profile.emailLabel")}:</span>{" "}
        <span className="text-nb-text">{me.data.user.email}</span>{" "}
        <span className="text-nb-text-3">
          ({t("account.profile.emailReadOnlyNote")})
        </span>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
        >
          {save.isPending
            ? t("account.profile.saving")
            : t("account.profile.saveButton")}
        </button>
        {save.isSuccess && !dirty && (
          <span className="text-xs text-cvp-ok">
            {t("account.profile.savedAck")}
          </span>
        )}
      </div>
      {save.isError && (
        <p className="text-xs text-cvp-danger">
          {(save.error as Error).message ??
            t("account.profile.saveErrorGeneric")}
        </p>
      )}
    </section>
  );
}


function ChangePasswordCard() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState(false);

  const submit = useMutation({
    mutationFn: async () => {
      const res = await api.post(ApiPaths.userChangePassword, {
        current_password: current,
        new_password: next,
      });
      return res.data;
    },
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
    },
  });

  const submitDisabled =
    !current || !next || next !== confirm || submit.isPending;

  return (
    <section className="card space-y-3">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("account.password.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">{t("account.password.cardHint")}</p>
      </header>
      <div className="grid gap-3">
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>{t("account.password.currentLabel")}</span>
          <PasswordInput
            autoComplete="current-password"
            className="w-full"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </label>
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>{t("account.password.newLabel")}</span>
          <PasswordInput
            autoComplete="new-password"
            className="w-full"
            value={next}
            onChange={(e) => {
              setNext(e.target.value);
              setMismatch(false);
            }}
            minLength={12}
          />
        </label>
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>{t("account.password.confirmLabel")}</span>
          <PasswordInput
            autoComplete="new-password"
            className="w-full"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              setMismatch(false);
            }}
            onBlur={() => setMismatch(!!confirm && next !== confirm)}
          />
        </label>
        {mismatch && (
          <p className="text-xs text-cvp-warn">
            {t("account.password.mismatchWarning")}
          </p>
        )}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={() => submit.mutate()}
          disabled={submitDisabled}
        >
          {submit.isPending
            ? t("account.password.saving")
            : t("account.password.saveButton")}
        </button>
        {submit.isSuccess && (
          <span className="text-xs text-cvp-ok">
            {t("account.password.savedAck")}
          </span>
        )}
      </div>
      {submit.isError && (
        <p className="text-xs text-cvp-danger">
          {(submit.error as Error).message ??
            t("account.password.saveErrorGeneric")}
        </p>
      )}
    </section>
  );
}


function MfaCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const [shownCodes, setShownCodes] = useState<string[] | null>(null);

  const backupStatus = useQuery({
    queryKey: ["mfa-backup-codes-status"],
    queryFn: async () =>
      (await api.get<{ remaining: number }>(ApiPaths.authMfaBackupCodes)).data,
    enabled: !!me.data?.user?.mfa_enabled,
  });

  const regenerate = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ backup_codes: string[] }>(
        ApiPaths.authMfaBackupCodesRegenerate,
      );
      return res.data.backup_codes;
    },
    onSuccess: (codes) => {
      setShownCodes(codes);
      void qc.invalidateQueries({ queryKey: ["mfa-backup-codes-status"] });
    },
  });

  if (!me.data?.user) return null;
  const enabled = me.data.user.mfa_enabled;
  const remaining = backupStatus.data?.remaining;

  return (
    <section className="card space-y-3">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("account.mfa.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">{t("account.mfa.cardHint")}</p>
      </header>
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="text-sm text-nb-text">
          <span className="text-nb-text-2">{t("account.mfa.statusLabel")}</span>{" "}
          <span
            className={enabled ? "font-medium text-cvp-ok" : "font-medium text-cvp-warn"}
          >
            {enabled
              ? t("account.mfa.statusEnabled")
              : t("account.mfa.statusDisabled")}
          </span>
        </span>
        {!enabled && (
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => setOpen(true)}
          >
            {t("account.mfa.enrollButton")}
          </button>
        )}
      </div>
      {!enabled && (
        <p className="text-xs text-nb-text-3">{t("account.mfa.requiredNote")}</p>
      )}

      {enabled && (
        <div className="space-y-2 border-t border-nb-border pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
            {t("account.mfa.backupCardTitle")}
          </h3>
          <p className="text-xs text-nb-text-3">
            {t("account.mfa.backupCardHint")}
          </p>
          {remaining !== undefined && (
            <p className="text-sm text-nb-text">
              <span className="text-nb-text-2">
                {t("account.mfa.backupRemainingLabel")}:
              </span>{" "}
              <span
                className={
                  remaining <= 2 ? "font-medium text-cvp-warn" : "font-medium"
                }
              >
                {t("account.mfa.backupRemainingValue", { count: remaining })}
              </span>
            </p>
          )}
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => {
              if (
                window.confirm(t("account.mfa.regenerateConfirm"))
              ) {
                regenerate.mutate();
              }
            }}
            disabled={regenerate.isPending}
          >
            {regenerate.isPending
              ? t("account.mfa.regenerating")
              : t("account.mfa.regenerateButton")}
          </button>
        </div>
      )}

      {open && (
        <MfaSetupModal
          onClose={() => setOpen(false)}
          onActivated={() => {
            setOpen(false);
            void qc.invalidateQueries({ queryKey: ["me"] });
            void qc.invalidateQueries({ queryKey: ["mfa-backup-codes-status"] });
          }}
        />
      )}

      {shownCodes && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("account.mfa.regenerateButton")}
          className="modal-backdrop bg-black/70"
        >
          <div className="modal-panel w-full max-w-md">
            <BackupCodesPanel
              codes={shownCodes}
              title={t("account.mfa.regenerateTitle")}
              onAcknowledged={() => setShownCodes(null)}
            />
          </div>
        </div>
      )}
    </section>
  );
}


function PasskeysCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const supported = browserSupportsWebAuthn();

  const passkeys = useQuery({
    queryKey: ["webauthn-credentials"],
    queryFn: listPasskeys,
    enabled: supported,
  });

  const add = useMutation({
    // Auto-named from the device (e.g. "Mac", "iPhone") — no prompt. The
    // passkey can be told apart by its name + added/last-used dates in the
    // list below; rename is a future nicety.
    mutationFn: () => registerPasskey(),
    onSuccess: () => {
      setErr(null);
      void qc.invalidateQueries({ queryKey: ["webauthn-credentials"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => {
      if ((e as Error)?.name === "NotAllowedError") return;
      setErr(
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? t("account.passkeys.addFailed", "Could not add passkey"),
      );
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deletePasskey(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["webauthn-credentials"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });

  if (!supported) return null;
  const rows = passkeys.data ?? [];

  return (
    <section className="card space-y-3">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("account.passkeys.cardTitle", "Passkeys")}
        </h2>
        <p className="text-xs text-nb-text-3">
          {t(
            "account.passkeys.cardHint",
            "Sign in without a password and satisfy MFA using your device biometrics or a security key.",
          )}
        </p>
      </div>
      {err && (
        <p role="alert" className="text-sm text-cvp-danger">
          {err}
        </p>
      )}
      <ul className="divide-y divide-nb-border">
        {rows.map((pk) => (
          <li
            key={pk.id}
            className="flex items-center justify-between gap-3 py-2"
          >
            <div className="min-w-0">
              <div className="truncate text-sm text-nb-text">{pk.name}</div>
              <div className="text-xs text-nb-text-3">
                {t("account.passkeys.added", "Added")}{" "}
                {formatDate(pk.created_at)}
                {pk.last_used_at &&
                  ` · ${t("account.passkeys.lastUsed", "last used")} ${formatDate(
                    pk.last_used_at,
                  )}`}
              </div>
            </div>
            <button
              type="button"
              className="btn-ghost shrink-0 text-xs text-cvp-danger"
              onClick={() => {
                if (
                  window.confirm(
                    t("account.passkeys.removeConfirm", "Remove this passkey?"),
                  )
                )
                  remove.mutate(pk.id);
              }}
              disabled={remove.isPending}
            >
              {t("common.remove", "Remove")}
            </button>
          </li>
        ))}
        {passkeys.isSuccess && rows.length === 0 && (
          <li className="py-2 text-xs text-nb-text-3">
            {t("account.passkeys.empty", "No passkeys yet.")}
          </li>
        )}
      </ul>
      <button
        type="button"
        className="btn-primary text-sm"
        onClick={() => add.mutate()}
        disabled={add.isPending}
      >
        {add.isPending
          ? t("common.saving")
          : t("account.passkeys.addButton", "Add a passkey")}
      </button>
    </section>
  );
}


export default function AccountProfilePage() {
  const { t } = useTranslation();
  return (
    <AppLayout
      eyebrow={t("account.eyebrow")}
      title={t("account.pageTitle")}
      description={t("account.pageDescription")}
    >
      <ProfileCard />
      <MfaCard />
      <PasskeysCard />
      <ChangePasswordCard />
    </AppLayout>
  );
}
